import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Save, RotateCcw, Palette, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preset color palette with names
const COLOR_PRESETS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Sky Blue', value: '#0ea5e9' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Dark Green', value: '#059669' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Fuchsia', value: '#d946ef' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Slate', value: '#64748b' },
  { name: 'Gray', value: '#6b7280' },
];

const DEFAULT_COLORS = {
  lead: '#3b82f6',
  intro_call: '#06b6d4',
  appointment_set: '#6366f1',
  demo: '#a855f7',
  pending_signature: '#f97316',
  signed_agreement: '#10b981',
  qc: '#14b8a6',
  production: '#eab308',
  scheduled: '#84cc16',
  crew_assigned: '#10b981',
  install: '#f59e0b',
  inspections: '#0ea5e9',
  invoice: '#8b5cf6',
  pending_payment: '#ec4899',
  paid: '#059669',
  lost_dnq: '#ef4444'
};

const WORKFLOW_STAGES = [
  { id: 'lead', name: 'Lead' },
  { id: 'intro_call', name: 'Intro Call' },
  { id: 'appointment_set', name: 'Appointment Set' },
  { id: 'demo', name: 'Demo' },
  { id: 'pending_signature', name: 'Pending Signature' },
  { id: 'signed_agreement', name: 'Signed Agreement' },
  { id: 'qc', name: 'QC' },
  { id: 'production', name: 'Production' },
  { id: 'scheduled', name: 'Scheduled' },
  { id: 'crew_assigned', name: 'Crew Assigned' },
  { id: 'install', name: 'Install' },
  { id: 'inspections', name: 'Inspections' },
  { id: 'invoice', name: 'Invoice' },
  { id: 'pending_payment', name: 'Pending Payment' },
  { id: 'paid', name: 'Paid' },
  { id: 'lost_dnq', name: 'Lost/DNQ' }
];

// Helper to find color name from value
const getColorName = (colorValue) => {
  const preset = COLOR_PRESETS.find(c => c.value.toLowerCase() === colorValue?.toLowerCase());
  return preset ? preset.name : 'Custom';
};

const WorkflowColorSettings = () => {
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchColorSettings();
  }, []);

  const fetchColorSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/workflow-colors`);
      if (response.data.colors && Object.keys(response.data.colors).length > 0) {
        setColors({ ...DEFAULT_COLORS, ...response.data.colors });
      }
    } catch (error) {
      console.error('Error fetching color settings:', error);
      // Use defaults if no settings found
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (stageId, newColor) => {
    setColors(prev => ({
      ...prev,
      [stageId]: newColor
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/workflow-colors`, { colors });
      toast.success('Workflow colors saved successfully!');
    } catch (error) {
      console.error('Error saving colors:', error);
      toast.error('Failed to save workflow colors');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all colors to defaults?')) {
      setColors(DEFAULT_COLORS);
      toast.success('Colors reset to defaults');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Palette className="h-8 w-8 text-primary" />
              Component Colors
            </h1>
            <p className="text-muted-foreground mt-2">
              Customize the colors for workflow stages and other components in the Jobs module
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Color Grid */}
        <Card>
          <CardHeader>
            <CardTitle>Workflow Stage Colors</CardTitle>
            <CardDescription>
              Select a color from the dropdown for each workflow stage. Colors will be applied to job status badges throughout the platform.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {WORKFLOW_STAGES.map((stage) => (
                <div
                  key={stage.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow bg-card"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-lg border-2 border-muted shadow-sm flex-shrink-0"
                      style={{ backgroundColor: colors[stage.id] }}
                    />
                    <Label className="font-medium text-sm">{stage.name}</Label>
                  </div>
                  
                  <Select
                    value={colors[stage.id]}
                    onValueChange={(value) => handleColorChange(stage.id, value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full border" 
                            style={{ backgroundColor: colors[stage.id] }}
                          />
                          <span>{getColorName(colors[stage.id])}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_PRESETS.map((preset) => (
                        <SelectItem key={preset.value} value={preset.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full border" 
                              style={{ backgroundColor: preset.value }}
                            />
                            <span>{preset.name}</span>
                            {colors[stage.id]?.toLowerCase() === preset.value.toLowerCase() && (
                              <Check className="h-4 w-4 ml-auto text-primary" />
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Preview Badge */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Preview:</span>
                      <span
                        className="px-3 py-1 rounded-full text-white text-xs font-medium shadow-sm"
                        style={{ backgroundColor: colors[stage.id] }}
                      >
                        {stage.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Live Preview Section */}
        <Card>
          <CardHeader>
            <CardTitle>Live Preview</CardTitle>
            <CardDescription>
              See how the colors will look on job cards throughout the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {WORKFLOW_STAGES.slice(0, 8).map((stage) => (
                <div key={stage.id} className="p-4 bg-muted/30 border rounded-lg">
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm">123 Main St</h3>
                    <p className="text-xs text-muted-foreground">John Smith</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">JOB-123456</span>
                      <span
                        className="px-2 py-0.5 rounded-full text-white text-xs font-medium"
                        style={{ backgroundColor: colors[stage.id] }}
                      >
                        {stage.name}
                      </span>
                    </div>
                    <div className="text-sm font-bold text-green-600">$15,000</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Workflow Tracker Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Workflow Tracker Preview</CardTitle>
            <CardDescription>
              Preview of the horizontal workflow tracker with your selected colors
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto pb-4">
              <div className="flex items-center gap-1 min-w-max">
                {WORKFLOW_STAGES.map((stage, index) => (
                  <div key={stage.id} className="flex items-center">
                    <div
                      className="px-3 py-1.5 rounded-full text-white text-xs font-medium whitespace-nowrap"
                      style={{ backgroundColor: colors[stage.id] }}
                    >
                      {stage.name}
                    </div>
                    {index < WORKFLOW_STAGES.length - 1 && (
                      <div className="w-4 h-0.5 bg-muted-foreground/30 mx-1" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default WorkflowColorSettings;
